import { create } from 'zustand'

import { getNavigationMenusApi, NavMenuItem } from '@/network/api/global'

const CACHE_KEY = 'yun3d_nav_menus'
const CACHE_TS_KEY = 'yun3d_nav_menus_ts'
const HIDDEN_CLIENT_MENU_PATHS = new Set(['/cnc', '/lamination'])

const resolveVisibleMenus = (menus: NavMenuItem[]): NavMenuItem[] =>
  menus
    .filter((menu) => !HIDDEN_CLIENT_MENU_PATHS.has(menu.path))
    .map((menu) => ({
      ...menu,
      children: menu.children ? resolveVisibleMenus(menu.children) : undefined,
    }))

interface NavigationMenusStore {
  menus: NavMenuItem[]
  loaded: boolean
  fetchMenus: (force?: boolean) => Promise<void>
}

const FALLBACK_MENUS: NavMenuItem[] = [
  {
    id: -1,
    title: 'AI 生成',
    title_i18n: { en: 'AI Generate', tw: 'AI 生成' },
    path: '/ai/imageTo3d',
    sort: 1,
    type: 'MENU',
    config: { badge: '3D', matchPattern: 'prefix:/ai' },
  },
  {
    id: -2,
    title: '3D 模型库',
    title_i18n: { en: '3D Model Library', tw: '3D 模型庫' },
    path: '/home',
    sort: 2,
    type: 'MENU',
    config: { matchPattern: 'prefix:/home' },
  },
  {
    id: -3,
    title: '3D设计',
    title_i18n: { en: '3D Design', tw: '3D設計' },
    path: '/model',
    sort: 3,
    type: 'MENU',
    config: { matchPattern: 'prefix:/model' },
  },
  {
    id: -4,
    title: '3D打印',
    title_i18n: { en: '3D Print', tw: '3D列印' },
    path: '/print',
    sort: 4,
    type: 'MENU',
    config: { matchPattern: 'prefix:/print' },
  },
  {
    id: -7,
    title: '3D扫描',
    title_i18n: { en: '3D Scanning', tw: '3D掃描' },
    path: '/scan',
    sort: 7,
    type: 'MENU',
    config: { matchPattern: 'prefix:/scan' },
  },
  {
    id: -8,
    title: '关于我们',
    title_i18n: { en: 'About Us', tw: '關於我們' },
    path: '/about',
    sort: 8,
    type: 'MENU',
    config: { matchPattern: 'exact:/about' },
  },
]

export const useNavigationMenusStore = create<NavigationMenusStore>((set, get) => ({
  menus: [],
  loaded: false,

  fetchMenus: async (force = false) => {
    if (!force) {
      const cached = localStorage.getItem(CACHE_KEY)
      if (cached) {
        try {
          const menus = resolveVisibleMenus(JSON.parse(cached) as NavMenuItem[])
          if (menus.length > 0) {
            set({ menus, loaded: true })
            // Silent background refresh
            getNavigationMenusApi()
              .then((res: any) => {
                if (res.code === 0 && res.data?.length) {
                  const visibleMenus = resolveVisibleMenus(res.data)
                  set({ menus: visibleMenus })
                  localStorage.setItem(CACHE_KEY, JSON.stringify(visibleMenus))
                  localStorage.setItem(CACHE_TS_KEY, Date.now().toString())
                }
              })
              .catch(() => {})
            return
          }
        } catch {
          /* corrupted cache, fetch from network */
        }
      }
    }

    try {
      const res = (await getNavigationMenusApi()) as any
      if (res.code === 0 && res.data?.length) {
        const visibleMenus = resolveVisibleMenus(res.data)
        set({ menus: visibleMenus, loaded: true })
        localStorage.setItem(CACHE_KEY, JSON.stringify(visibleMenus))
        localStorage.setItem(CACHE_TS_KEY, Date.now().toString())
      } else {
        if (!get().loaded) set({ menus: FALLBACK_MENUS, loaded: true })
      }
    } catch {
      if (!get().loaded) set({ menus: FALLBACK_MENUS, loaded: true })
    }
  },
}))
